import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, MessageCircle } from "lucide-react";
import { SHOP_ITEMS, SITE } from "@/lib/site";

const orderUrl = (itemName) =>
  `https://wa.me/${SITE.phoneClean}?text=${encodeURIComponent(
    `Namaste Archana, I'd like to order the "${itemName}" from your shop. Please share the next steps.`
  )}`;

const Shop = () => {
  return (
    <section id="shop" data-testid="shop-section" className="py-24 lg:py-32 bg-[#FAF9F6] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 right-0 h-[32rem] w-[32rem] rounded-full bg-[#C5A880]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-[#B35B47]/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#B35B47]">
              <Sparkles className="h-3.5 w-3.5 text-[#C5A880]" />
              — Festive Shop · Diwali Edition
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#2C302E] leading-[1.05]">
              Sacred objects,<br />lovingly curated.
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#5C605A] leading-relaxed">
            Hand-selected crystals, intention kits and festive blessings —
            each item is energetically cleansed and charged with Reiki before
            it leaves my hands. Order via WhatsApp.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SHOP_ITEMS.map((item, i) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              data-testid={`shop-item-${item.id}`}
              className="group relative bg-[#F4F1EB] rounded-3xl overflow-hidden border border-[#EAE5D9] hover:border-[#C5A880]/60 transition-all duration-500 flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#EAE5D9]">
                <img
                  src={item.image}
                  alt={item.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#2C302E]/85 backdrop-blur-sm text-[#FAF9F6] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                  <Sparkles className="h-3 w-3 text-[#C5A880]" />
                  {item.tagline}
                </span>
              </div>

              <div className="p-7 lg:p-8 flex-1 flex flex-col">
                <h3 className="font-serif text-2xl lg:text-[1.65rem] text-[#2C302E] leading-tight">
                  {item.name}
                </h3>
                <div className="mt-2 font-serif text-xl text-[#B35B47]">{item.price}</div>
                <p className="mt-4 text-[#5C605A] leading-relaxed text-sm">
                  {item.description}
                </p>

                <ul className="mt-5 flex flex-wrap gap-2">
                  {item.highlights.map((h) => (
                    <li
                      key={h}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#EAE5D9]/70 text-[#2C302E] text-xs px-3 py-1.5"
                    >
                      <Sparkles className="h-3 w-3 text-[#C5A880]" /> {h}
                    </li>
                  ))}
                </ul>

                <a
                  href={orderUrl(item.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shop-order-${item.id}`}
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#2C302E] text-[#FAF9F6] px-6 py-3 text-sm tracking-wide hover:bg-[#B35B47] transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  Order on WhatsApp
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.22em] text-[#8A8D86]">
          <ShoppingBag className="h-3.5 w-3.5" />
          Pan-India shipping · Energy-cleansed before dispatch
        </div>
      </div>
    </section>
  );
};

export default Shop;
